import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, FileText, Camera, Upload, RotateCcw, Trash2, X, CreditCard, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import FormField, { fieldClass } from './FormField';
import { ID_CARD_MAX } from '../../utils/bookingValidation';
import { compressImage, formatFileSize } from '../../utils/imageCompression';

const ACCEPTED = 'image/jpeg,image/png,image/gif,image/webp';

/**
 * ID proof capture: upload a photo, take one with the camera, or type an ID number.
 *
 * Shared by the customer page and the admin modal so both offer the same options — the
 * public form previously had a bare file input with no camera and no preview.
 *
 * Photos are downscaled before they leave the browser. Straight-from-the-camera JPEGs are
 * several megabytes, which the API rejects and the hosting proxy can truncate.
 */
const IdProofInput = ({
  inputType,
  onInputTypeChange,
  imageFile,
  onImageChange,
  idCardNumber,
  onIdCardNumberChange,
  error,
  disabled = false
}) => {
  const [preview, setPreview] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [processing, setProcessing] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCameraOpen(false);
  }, []);

  // Release the camera and the object URL when the form closes or unmounts — without this
  // the camera light stays on after the modal is dismissed.
  useEffect(() => () => {
    stopCamera();
  }, [stopCamera]);

  useEffect(() => {
    if (!imageFile) {
      setPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(imageFile);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const acceptFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast.error('Please choose an image file (JPG, PNG, GIF or WebP)');
      return;
    }
    setProcessing(true);
    try {
      const compressed = await compressImage(file);
      onImageChange(compressed);
    } finally {
      setProcessing(false);
    }
  }, [onImageChange]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    // Reset the input so re-picking the same file fires change again.
    e.target.value = '';
    stopCamera();
    await acceptFile(file);
  };

  const startCamera = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error('This browser cannot access the camera. Please upload a photo instead.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;
      setCameraOpen(true);
      setTimeout(() => {
        if (videoRef.current) videoRef.current.srcObject = stream;
      }, 100);
    } catch (err) {
      console.error('Camera error:', err);
      toast.error('Unable to access the camera. Check browser permissions, or upload a photo.');
    }
  }, []);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      stopCamera();
      await acceptFile(new File([blob], `id-card-${Date.now()}.jpg`, { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.85);
  }, [acceptFile, stopCamera]);

  const clearImage = () => {
    onImageChange(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const switchTo = (type) => {
    if (type === 'text') stopCamera();
    onInputTypeChange(type);
  };

  const toggleClass = (active, activeColor) =>
    `flex-1 py-2.5 px-3 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-1.5 ${
      active ? `${activeColor} text-white shadow` : 'text-midnight-600 hover:bg-midnight-100'
    }`;

  return (
    <FormField label="ID Proof" required error={error}>
      <div className="flex gap-2 bg-midnight-50 rounded-xl p-1.5 mb-2">
        <button
          type="button"
          onClick={() => switchTo('image')}
          disabled={disabled}
          className={toggleClass(inputType === 'image', 'bg-railway-500')}
        >
          <ImageIcon className="w-4 h-4" />
          Upload Image
        </button>
        <button
          type="button"
          onClick={() => switchTo('text')}
          disabled={disabled}
          className={toggleClass(inputType === 'text', 'bg-railway-500')}
        >
          <FileText className="w-4 h-4" />
          Enter ID Number
        </button>
      </div>

      {inputType === 'image' ? (
        <>
          {preview && !cameraOpen && (
            <div className="relative mb-2 rounded-lg overflow-hidden border border-midnight-200 bg-midnight-50">
              <img src={preview} alt="ID card preview" className="w-full h-40 object-contain" />
              <button
                type="button"
                onClick={clearImage}
                disabled={disabled}
                className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors shadow"
                title="Remove image"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
              {imageFile && (
                <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded bg-midnight-900/70 text-white text-[11px]">
                  {formatFileSize(imageFile.size)}
                </span>
              )}
            </div>
          )}

          {cameraOpen && (
            <div className="relative mb-2 rounded-lg overflow-hidden border-2 border-indigo-300 bg-black">
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-48 object-cover" />
              <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-3">
                <button
                  type="button"
                  onClick={capturePhoto}
                  className="px-4 py-2 bg-white text-midnight-800 rounded-full font-medium text-sm shadow-lg hover:bg-midnight-50 transition-colors flex items-center gap-1.5"
                >
                  <Camera className="w-4 h-4" />
                  Capture
                </button>
                <button
                  type="button"
                  onClick={stopCamera}
                  className="px-4 py-2 bg-red-500 text-white rounded-full font-medium text-sm shadow-lg hover:bg-red-600 transition-colors flex items-center gap-1.5"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
              </div>
              <canvas ref={canvasRef} className="hidden" />
            </div>
          )}

          {processing && (
            <div className="mb-2 flex items-center gap-2 text-sm text-midnight-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Optimising image…
            </div>
          )}

          {!cameraOpen && !preview && !processing && (
            <div className="flex gap-2">
              <label className={`flex-1 flex items-center justify-center gap-2 py-3 px-3 border-2 border-dashed rounded-lg transition-all text-sm text-midnight-600 ${
                disabled ? 'border-midnight-100 cursor-not-allowed' : 'border-midnight-200 cursor-pointer hover:border-railway-400 hover:bg-railway-50/50'
              }`}>
                <Upload className="w-4 h-4" />
                <span>Upload File</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileChange}
                  accept={ACCEPTED}
                  disabled={disabled}
                  className="hidden"
                />
              </label>
              <button
                type="button"
                onClick={startCamera}
                disabled={disabled}
                className="flex-1 flex items-center justify-center gap-2 py-3 px-3 border-2 border-dashed border-midnight-200 rounded-lg hover:border-indigo-400 hover:bg-indigo-50/50 transition-all text-sm text-midnight-600 disabled:cursor-not-allowed"
              >
                <Camera className="w-4 h-4" />
                <span>Open Camera</span>
              </button>
            </div>
          )}

          {preview && !cameraOpen && (
            <div className="flex gap-2">
              <label className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 bg-midnight-50 rounded-lg cursor-pointer hover:bg-midnight-100 transition-colors text-sm text-midnight-600">
                <Upload className="w-3.5 h-3.5" />
                <span>Re-upload</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileChange}
                  accept={ACCEPTED}
                  disabled={disabled}
                  className="hidden"
                />
              </label>
              <button
                type="button"
                onClick={() => { clearImage(); startCamera(); }}
                disabled={disabled}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 bg-midnight-50 rounded-lg hover:bg-midnight-100 transition-colors text-sm text-midnight-600"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>Retake</span>
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="relative">
          <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-midnight-400 pointer-events-none" />
          <input
            type="text"
            value={idCardNumber}
            onChange={(e) => onIdCardNumberChange(e.target.value.slice(0, ID_CARD_MAX))}
            disabled={disabled}
            maxLength={ID_CARD_MAX}
            placeholder="Aadhaar / PAN / Passport number"
            className={fieldClass(error, 'pl-10')}
          />
          <p className="text-xs text-midnight-500 mt-1.5">
            Government-issued ID number · letters, digits, spaces, - and / only
          </p>
        </div>
      )}
    </FormField>
  );
};

export default IdProofInput;
